import { PageLoader } from '@/components/ui/loader';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Code2, Copy, ExternalLink, FileInput, Inbox, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Select, Switch, Textarea } from '@/components/ui/misc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { FormView } from './public-form';
import {
  useDeleteForm,
  useFormSubmissions,
  useForms,
  useSaveForm,
  type FieldType,
  type FormField,
  type FormSettings,
  type MarketingForm,
} from './api';

const FIELD_PRESETS: { key: string; label: string; type: FieldType; width?: 'half' }[] = [
  { key: 'fullName', label: 'Full name', type: 'text' },
  { key: 'firstName', label: 'First name', type: 'text', width: 'half' },
  { key: 'lastName', label: 'Last name', type: 'text', width: 'half' },
  { key: 'email', label: 'Email', type: 'email', width: 'half' },
  { key: 'phone', label: 'Mobile', type: 'phone', width: 'half' },
  { key: 'whatsapp', label: 'WhatsApp', type: 'phone', width: 'half' },
  { key: 'companyName', label: 'Company', type: 'text', width: 'half' },
  { key: 'designation', label: 'Job title', type: 'text', width: 'half' },
  { key: 'city', label: 'City / emirate', type: 'select', width: 'half' },
  { key: 'budget', label: 'Budget (AED)', type: 'number', width: 'half' },
  { key: 'requirement', label: 'How can we help?', type: 'textarea' },
];

const TYPE_LABELS: Record<FieldType, string> = {
  text: 'Short text',
  email: 'Email',
  phone: 'Phone',
  textarea: 'Long text',
  select: 'Dropdown',
  checkbox: 'Tick box',
  number: 'Number',
  date: 'Date',
  hidden: 'Hidden value',
};

const EMIRATES = ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain'];

let counter = 0;
const newId = () => `f${Date.now().toString(36).slice(-4)}${(counter += 1)}`;

const DEFAULT_SETTINGS: FormSettings = {
  title: 'Talk to our team',
  description: 'Leave your details and we will call you back within the hour.',
  submitLabel: 'Send',
  successMessage: 'Thank you! We will be in touch shortly.',
  redirectUrl: '',
  downloadUrl: '',
  unsorted: true,
  ownerId: null,
  notifyUserIds: [],
  matchContacts: true,
  consentText: '',
  requireConsent: false,
  campaign: '',
  colour: '#2563eb',
  popup: { enabled: false, trigger: 'delay', delaySeconds: 8, scrollPercent: 50, pages: [], frequencyDays: 7, position: 'center' },
};

function starterFields(): FormField[] {
  return [
    { id: newId(), key: 'fullName', label: 'Full name', type: 'text', required: true, width: 'full' },
    { id: newId(), key: 'email', label: 'Email', type: 'email', required: true, width: 'half' },
    { id: newId(), key: 'phone', label: 'Mobile', type: 'phone', required: true, width: 'half' },
    { id: newId(), key: 'requirement', label: 'How can we help?', type: 'textarea', required: false, width: 'full' },
  ];
}

/** Forms and pop-ups (HubSpot forms, AmoCRM web forms). */
export function FormsPage() {
  const { data: forms = [], isLoading } = useForms();
  const [editing, setEditing] = useState<MarketingForm | 'new' | null>(null);
  const [sharing, setSharing] = useState<MarketingForm | null>(null);
  const [viewing, setViewing] = useState<MarketingForm | null>(null);

  return (
    <div>
      <PageHeader
        title="Forms & pop-ups"
        description="Web-to-lead forms for your website, landing pages and pop-ups. Every submission becomes an incoming lead with where it came from."
        breadcrumbs={[{ label: 'Marketing hub' }, { label: 'Forms & pop-ups' }]}
        actions={
          <Can permission="marketing.form.create">
            <Button onClick={() => setEditing('new')}>
              <Plus /> New form
            </Button>
          </Can>
        }
      />
      {isLoading ? (
        <PageLoader />
      ) : forms.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-10 text-center">
          <FileInput className="mx-auto h-10 w-10 text-primary/60" />
          <p className="mt-3 font-semibold">No forms yet</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">Start with “Talk to our team”: name, email, mobile and a message.</p>
          <Can permission="marketing.form.create">
            <Button className="mt-4" onClick={() => setEditing('new')}>
              Create a form
            </Button>
          </Can>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {forms.map((form) => (
            <Card key={form.id}>
              <CardContent className="space-y-3 pt-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{form.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {form.fields.length} fields{form.settings.popup?.enabled ? ' · pop-up on' : ''}
                    </p>
                  </div>
                  <Badge className={form.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-200 text-neutral-600'}>{form.isActive ? 'Live' : 'Off'}</Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {(
                    [
                      ['Views', form.viewCount],
                      ['Leads', form.submissionCount],
                      ['Converts', `${form.conversionRate}%`],
                    ] as const
                  ).map(([label, value]) => (
                    <div key={label} className="rounded-lg bg-muted/40 py-2">
                      <p className="text-base font-bold">{value}</p>
                      <p className="text-[0.65rem] uppercase tracking-wide text-muted-foreground">{label}</p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1">
                  <Button size="sm" variant="outline" onClick={() => setSharing(form)}>
                    <Code2 /> Share & embed
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setViewing(form)}>
                    <Inbox /> Submissions
                  </Button>
                  <Can permission="marketing.form.update">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(form)}>
                      Edit
                    </Button>
                  </Can>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {editing && <FormBuilder form={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {sharing && <ShareDialog form={sharing} onClose={() => setSharing(null)} />}
      {viewing && <SubmissionsDialog form={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function ShareDialog({ form, onClose }: { form: MarketingForm; onClose: () => void }) {
  const copy = async (value: string) => {
    await navigator.clipboard.writeText(value).catch(() => undefined);
    toast.success('Copied');
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Share “{form.name}”</DialogTitle>
          <DialogDescription>Use the link anywhere, or paste the code into your website.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {(
            [
              ['Link', form.url],
              ['Embed code (paste where the form should appear)', form.embedCode],
            ] as const
          ).map(([label, value]) => (
            <div key={label} className="space-y-1.5">
              <Label>{label}</Label>
              <div className="flex gap-2">
                <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap rounded-lg bg-muted/60 px-3 py-2 text-xs">{value}</code>
                <Button size="sm" variant="outline" onClick={() => copy(value)}>
                  <Copy />
                </Button>
              </div>
            </div>
          ))}
          <p className="rounded-lg bg-primary/5 p-3 text-sm text-muted-foreground">
            For a pop-up, turn it on in the form’s settings and add the website snippet once — see{' '}
            <Link to="/marketing/website" className="text-primary underline">
              Website & tracking
            </Link>
            . The snippet also links each lead to the pages they read.
          </p>
          <Button variant="outline" asChild>
            <a href={form.url} target="_blank" rel="noreferrer">
              <ExternalLink /> Open the form
            </a>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SubmissionsDialog({ form, onClose }: { form: MarketingForm; onClose: () => void }) {
  const { data: rows = [], isLoading } = useFormSubmissions(form.id);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Submissions — {form.name}</DialogTitle>
          <DialogDescription>The latest 200, each with the lead or contact it went to.</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <PageLoader />
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Nothing yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Answers</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Record</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{formatDateTime(row.createdAt)}</TableCell>
                    <TableCell className="max-w-sm text-xs">
                      {Object.entries(row.data)
                        .map(([key, value]) => `${form.fields.find((field) => field.key === key)?.label ?? key}: ${String(value)}`)
                        .join(' · ')}
                    </TableCell>
                    <TableCell className="text-xs">{[row.utm?.source, row.utm?.campaign].filter(Boolean).join(' / ') || 'direct'}</TableCell>
                    <TableCell className="text-xs">
                      {row.leadId ? (
                        <Link className="text-primary underline" to={`/crm/leads/${row.leadId}`}>
                          Lead
                        </Link>
                      ) : row.contactId ? (
                        <Link className="text-primary underline" to={`/crm/contacts/${row.contactId}`}>
                          Contact
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FormBuilder({ form, onClose }: { form: MarketingForm | null; onClose: () => void }) {
  const save = useSaveForm();
  const remove = useDeleteForm();
  const { data: users = [] } = useUserOptions();
  const [tab, setTab] = useState<'fields' | 'after' | 'popup' | 'style'>('fields');
  const [name, setName] = useState(form?.name ?? 'Contact us');
  const [fields, setFields] = useState<FormField[]>(form?.fields ?? starterFields());
  const [settings, setSettings] = useState<FormSettings>({ ...DEFAULT_SETTINGS, ...(form?.settings ?? {}), popup: { ...DEFAULT_SETTINGS.popup, ...(form?.settings.popup ?? {}) } });
  const [isActive, setIsActive] = useState(form?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof FormSettings>(key: K, value: FormSettings[K]) => setSettings((current) => ({ ...current, [key]: value }));
  const setPopup = (patch: Partial<FormSettings['popup']>) => setSettings((current) => ({ ...current, popup: { ...current.popup, ...patch } }));
  const update = (index: number, patch: Partial<FormField>) => setFields((current) => current.map((field, at) => (at === index ? { ...field, ...patch } : field)));
  const move = (index: number, by: number) =>
    setFields((current) => {
      const next = [...current];
      const [item] = next.splice(index, 1);
      next.splice(index + by, 0, item);
      return next;
    });
  const used = new Set(fields.map((field) => field.key));

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({
        id: form?.id,
        name,
        fields,
        settings: { ...settings, redirectUrl: settings.redirectUrl || '', downloadUrl: settings.downloadUrl || '' },
        isActive,
      });
      toast.success('Form saved');
      onClose();
    } catch (failure) {
      setError(failure instanceof ApiRequestError ? failure.message : 'Could not save');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[94vh] max-w-6xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{form ? 'Edit form' : 'New form'}</DialogTitle>
          <DialogDescription>The preview on the right is exactly what visitors see.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 lg:grid-cols-[1fr_24rem]">
          <div className="min-w-0 space-y-4">
            <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
              <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Form name (only you see it)" />
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={isActive} onCheckedChange={setIsActive} /> Live
              </label>
            </div>
            <div className="flex gap-1 border-b">
              {(
                [
                  ['fields', 'Fields'],
                  ['after', 'After sending'],
                  ['popup', 'Pop-up'],
                  ['style', 'Look'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  className={cn('-mb-px border-b-2 px-3 py-2 text-sm', tab === key ? 'border-primary font-semibold text-primary' : 'border-transparent text-muted-foreground')}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === 'fields' && (
              <div className="space-y-2">
                {fields.map((field, index) => (
                  <div key={field.id} className="space-y-2 rounded-xl border p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Input className="min-w-[10rem] flex-1" value={field.label} onChange={(event) => update(index, { label: event.target.value })} />
                      <Select className="w-36" value={field.type} onChange={(event) => update(index, { type: event.target.value as FieldType })}>
                        {Object.entries(TYPE_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Select>
                      <Select className="w-28" value={field.width} onChange={(event) => update(index, { width: event.target.value as 'full' | 'half' })}>
                        <option value="full">Full width</option>
                        <option value="half">Half</option>
                      </Select>
                      <label className="flex items-center gap-1 text-xs">
                        <Checkbox checked={field.required} onCheckedChange={(value) => update(index, { required: Boolean(value) })} /> Required
                      </label>
                      <Button size="sm" variant="ghost" disabled={index === 0} onClick={() => move(index, -1)}>
                        <ArrowUp />
                      </Button>
                      <Button size="sm" variant="ghost" disabled={index === fields.length - 1} onClick={() => move(index, 1)}>
                        <ArrowDown />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setFields((current) => current.filter((_, at) => at !== index))}>
                        <Trash2 />
                      </Button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span>Saved to</span>
                      <Input
                        className="h-8 w-40 text-xs"
                        value={field.key}
                        onChange={(event) => update(index, { key: event.target.value.replace(/[^\w.]/g, '') })}
                      />
                      {field.type === 'hidden' ? (
                        <Input className="h-8 flex-1 text-xs" placeholder="Fixed value" value={field.defaultValue ?? ''} onChange={(event) => update(index, { defaultValue: event.target.value })} />
                      ) : (
                        <Input className="h-8 flex-1 text-xs" placeholder="Placeholder" value={field.placeholder ?? ''} onChange={(event) => update(index, { placeholder: event.target.value })} />
                      )}
                    </div>
                    {field.type === 'select' && (
                      <Input
                        placeholder="Options, separated by commas"
                        value={(field.options ?? []).join(', ')}
                        onChange={(event) => update(index, { options: event.target.value.split(',').map((option) => option.trim()).filter(Boolean) })}
                      />
                    )}
                  </div>
                ))}
                <div className="flex flex-wrap gap-1.5">
                  {FIELD_PRESETS.filter((preset) => !used.has(preset.key)).map((preset) => (
                    <Button
                      key={preset.key}
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setFields((current) => [
                          ...current,
                          {
                            id: newId(),
                            key: preset.key,
                            label: preset.label,
                            type: preset.type,
                            required: false,
                            width: preset.width ?? 'full',
                            ...(preset.key === 'city' ? { options: EMIRATES } : {}),
                          },
                        ])
                      }
                    >
                      <Plus /> {preset.label}
                    </Button>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => setFields((current) => [...current, { id: newId(), key: `question${current.length + 1}`, label: 'Your question', type: 'text', required: false, width: 'full' }])}>
                    <Plus /> Own question
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  “Saved to” decides where the answer goes: a lead field, <code>cf.yourKey</code> for a custom field, or any other name to keep it as an answer on the lead.
                </p>
              </div>
            )}

            {tab === 'after' && (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Thank-you message</Label>
                  <Textarea rows={2} value={settings.successMessage} onChange={(event) => set('successMessage', event.target.value)} />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Then go to (optional)</Label>
                    <Input value={settings.redirectUrl ?? ''} onChange={(event) => set('redirectUrl', event.target.value)} placeholder="https://acme.ae/thank-you" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Lead magnet download (optional)</Label>
                    <Input value={settings.downloadUrl ?? ''} onChange={(event) => set('downloadUrl', event.target.value)} placeholder="https://… brochure.pdf" />
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Give leads to</Label>
                    <Select value={settings.ownerId ?? ''} onChange={(event) => set('ownerId', event.target.value || null)}>
                      <option value="">Sales managers decide</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Campaign tag</Label>
                    <Input value={settings.campaign ?? ''} onChange={(event) => set('campaign', event.target.value)} placeholder="Used when the page has no UTM tags" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Also tell</Label>
                  <div className="grid max-h-32 gap-1 overflow-y-auto sm:grid-cols-2">
                    {users.map((user) => (
                      <label key={user.id} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={settings.notifyUserIds.includes(user.id)}
                          onCheckedChange={(value) => set('notifyUserIds', value ? [...settings.notifyUserIds, user.id] : settings.notifyUserIds.filter((id) => id !== user.id))}
                        />
                        {user.name}
                      </label>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={settings.unsorted} onCheckedChange={(value) => set('unsorted', value)} /> New leads wait in Incoming to be accepted
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={settings.matchContacts} onCheckedChange={(value) => set('matchContacts', value)} /> A known contact’s submission goes on their record instead
                </label>
                <div className="space-y-1.5">
                  <Label>Consent text (optional)</Label>
                  <Input value={settings.consentText ?? ''} onChange={(event) => set('consentText', event.target.value)} placeholder="I agree to receive updates by email and WhatsApp." />
                  <label className="flex items-center gap-2 text-sm">
                    <Switch checked={settings.requireConsent} onCheckedChange={(value) => set('requireConsent', value)} /> They must tick it to send
                  </label>
                </div>
              </div>
            )}

            {tab === 'popup' && (
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <Switch checked={settings.popup.enabled} onCheckedChange={(value) => setPopup({ enabled: value })} /> Show as a pop-up on my website
                </label>
                <p className="text-xs text-muted-foreground">
                  Needs the website snippet from{' '}
                  <Link className="text-primary underline" to="/marketing/website">
                    Website & tracking
                  </Link>
                  .
                </p>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label>When</Label>
                    <Select value={settings.popup.trigger} onChange={(event) => setPopup({ trigger: event.target.value as FormSettings['popup']['trigger'] })}>
                      <option value="delay">After some seconds</option>
                      <option value="scroll">After scrolling</option>
                      <option value="exit">When about to leave</option>
                    </Select>
                  </div>
                  {settings.popup.trigger === 'delay' && (
                    <div className="space-y-1.5">
                      <Label>Seconds</Label>
                      <Input type="number" min={0} value={settings.popup.delaySeconds} onChange={(event) => setPopup({ delaySeconds: Number(event.target.value) || 0 })} />
                    </div>
                  )}
                  {settings.popup.trigger === 'scroll' && (
                    <div className="space-y-1.5">
                      <Label>Scrolled %</Label>
                      <Input type="number" min={5} max={100} value={settings.popup.scrollPercent} onChange={(event) => setPopup({ scrollPercent: Number(event.target.value) || 50 })} />
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label>Where</Label>
                    <Select value={settings.popup.position} onChange={(event) => setPopup({ position: event.target.value as FormSettings['popup']['position'] })}>
                      <option value="center">Centre of the screen</option>
                      <option value="bottom-right">Bottom right</option>
                      <option value="bottom-left">Bottom left</option>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Show again after (days)</Label>
                    <Input type="number" min={0} value={settings.popup.frequencyDays} onChange={(event) => setPopup({ frequencyDays: Number(event.target.value) || 0 })} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Only on these pages (one per line, * as wildcard; empty = everywhere)</Label>
                  <Textarea
                    rows={3}
                    value={settings.popup.pages.join('\n')}
                    onChange={(event) => setPopup({ pages: event.target.value.split('\n').map((line) => line.trim()).filter(Boolean) })}
                    placeholder={'/pricing\n/blog/*'}
                  />
                </div>
              </div>
            )}

            {tab === 'style' && (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Heading</Label>
                  <Input value={settings.title ?? ''} onChange={(event) => set('title', event.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Text under the heading</Label>
                  <Textarea rows={2} value={settings.description ?? ''} onChange={(event) => set('description', event.target.value)} />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Button</Label>
                    <Input value={settings.submitLabel} onChange={(event) => set('submitLabel', event.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Colour</Label>
                    <div className="flex gap-2">
                      <input type="color" className="h-10 w-14 rounded-lg border" value={settings.colour} onChange={(event) => set('colour', event.target.value)} />
                      <Input value={settings.colour} onChange={(event) => set('colour', event.target.value)} />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <FormError message={error} />
          </div>

          <div className="lg:sticky lg:top-0">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preview</p>
            <div className="pointer-events-none rounded-2xl border bg-white p-5 shadow-card">
              <FormView form={{ key: 'preview', name, company: '', fields, settings }} />
            </div>
          </div>
        </div>
        <DialogFooter className={form ? 'sm:justify-between' : undefined}>
          {form && (
            <Can permission="marketing.form.delete">
              <Button variant="ghost" className="text-destructive" onClick={() => window.confirm('Delete this form? Leads it made stay.') && remove.mutate(form.id, { onSuccess: onClose })}>
                <Trash2 /> Delete
              </Button>
            </Can>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
